import React, { useState, useEffect } from "react";
import {
  FolderPlus,
  HandHelping,
  FileSpreadsheet,
  FileText,
  IndianRupee,
  LandPlot,
  UsersRound,
  MessageSquareWarning,
  CalendarClock,
  Scale,
  Layers,
  UserCheck,
  FileBadge2,
  Award,
  Gavel,
  Wallet,
  IdCard,
  Search,
  FileSearch,
  Building2,
  ClipboardList,
  MapPinned,
  FileCheck,
  HousePlus,
  Landmark,
  LayoutTemplate,
  CreditCard,
  Shield,
  Ruler,
  UserCog,
  Clock,
  Users,
  MapPin,
  Home,
  Building,
  Briefcase,
  Flag,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  ChevronUp,
  LogOut,
} from "lucide-react";
import { useLanguage } from "../StoreContext/LanguageContext";
import apiService from "../config";
import { useProjectCategory } from "../Context/ProjectCategoryContext";

const Sidebar = ({ setSelectedComponent, currentComponent }) => {
  const { language, translations } = useLanguage();
  const [collapsed, setCollapsed] = useState(true);
  const [activeItem, setActiveItem] = useState("dashboard");
  const [hoveredItem, setHoveredItem] = useState(null);

  const APP_VERSION = "2.0.38";
  // Version 2.0.38

  const { projectCategory } = useProjectCategory();

  // Initialize userRole from localStorage immediately to prevent flash
  const [userRole, setUserRole] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        return user.user_role || null;
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
    return null;
  });

  const [userRoleName, setUserRoleName] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        return user.user_role_name || null;
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
    return null;
  });

  const [isSubUser, setIsSubUser] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        // User is a sub-user if work_assign is filled and sub_user_id is null/empty
        const isSubUserCheck = user.work_assign && !user.sub_user_id;

        // Set isAdmin in localStorage
        localStorage.setItem("isAdmin", !isSubUserCheck);

        return isSubUserCheck;
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
    // Default to sub-user (false) if no data
    localStorage.setItem("isAdmin", false);
    return false;
  });

  const [workAssignments, setWorkAssignments] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const user = JSON.parse(userData);
        const workAssignStr = user.work_assign || "";

        // Parse comma-separated string to array of numbers
        if (workAssignStr) {
          const assignIds = workAssignStr
            .split(",")
            .map((id) => parseInt(id.trim()))
            .filter((id) => !isNaN(id));
          console.log("Initial work assignment IDs:", assignIds);
          return assignIds;
        }
      }
    } catch (error) {
      console.error("Error parsing work assignments:", error);
    }
    return [];
  });

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // New state to track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState({
    Administration: false,
    "Land Management": false,
    Projects: true,
  });

  const WORK_ASSIGNMENTS = {
    1: { work_name: "Project Details", menu_ids: ["project-management"] },
    2: { work_name: "Proposal details", menu_ids: ["Proposal"] },
    3: { work_name: "Land Acqisition", menu_ids: ["landholding-list"] },
    4: { work_name: "Scetion-11", menu_ids: ["publish-proposal"] },
    5: { work_name: "JMR Section-12", menu_ids: ["JointMesurmentList"] },
    7: { work_name: "Advance Payment", menu_ids: ["AdvancePayment"] },
    8: { work_name: "Section-15", menu_ids: ["publish-proposal"] },
    9: { work_name: "Section-19", menu_ids: ["Section19"] },
    10: { work_name: "Section-21", menu_ids: ["Section21", "Section21_1_4"] },
    11: { work_name: "Market Value", menu_ids: ["MarketValue"] },
    13: { work_name: "Section-23", menu_ids: ["Final-Award"] },
    14: { work_name: "Compensation", menu_ids: ["SelectFormat"] },
    15: { work_name: "Direct Purchase", menu_ids: ["DirectPayment"] },
    16: { work_name: "Section-37(2)", menu_ids: ["ObjectionList"] },
    17: { work_name: "KJP", menu_ids: [] }, // Add appropriate menu_id
    18: { work_name: "Payment", menu_ids: ["Payment"] },
    19: { work_name: "PAP", menu_ids: ["PAP"] },
    20: { work_name: "Punarvarshan", menu_ids: ["Punavarshan"] },
    21: { work_name: "Family Form", menu_ids: ["FamilySurveyForm"] },
    22: { work_name: "Extend Date -section11", menu_ids: ["ExtendedDate"] },
    23: { work_name: "Extend Date - section 19", menu_ids: ["ExtendedDate19"] },
    24: {
      work_name: "Fund Request/Received",
      menu_ids: ["FundRequestReceived"],
    },
    25: { work_name: "Land Record Updation", menu_ids: ["Tehsildar"] },
    26: { work_name: "Section 93", menu_ids: ["Section93"] },
  };

  // Updated handleLogout function to use API service
  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple logout attempts
    setIsLoggingOut(true);

    try {
      console.log("Starting logout process...");

      // Log current localStorage state for debugging
      console.log("LocalStorage before clearing:", Object.keys(localStorage));
      console.log(
        "UserData before clearing:",
        localStorage.getItem("userData"),
      );

      // Call the API service logout method
      await apiService.logout();

      localStorage.clear();
      sessionStorage.clear();

      // Clear ALL localStorage data - comprehensive cleanup
      const keysToRemove = [
        "userData",
        "authToken",
        "userRole",
        "refreshToken",
        "selectedProject",
        "currentProjectData",
        "isAdmin",
        // Add any other keys you want to specifically clear
      ];

      // Remove specific keys
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });

      // Alternative: Clear ALL localStorage (use with caution)
      // localStorage.clear(); // This removes everything

      // Clear sessionStorage as well if you're using it
      sessionStorage.clear();

      // Reset component states
      setUserRole(null);
      setActiveItem("dashboard");
      setExpandedCategories({
        Administration: false,
        "Land Management": false,
        Projects: true,
      });

      // Redirect to home/login page
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);

      // Even if API call fails, clear local storage and redirect
      // This ensures user is logged out locally

      // Clear specific keys
      const keysToRemove = [
        "userData",
        "authToken",
        "userRole",
        "refreshToken",
        "selectedProject",
        "currentProjectData",
      ];

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });

      // Clear sessionStorage
      sessionStorage.clear();

      // Reset states
      setUserRole(null);

      // Show error message to user (optional)
      alert(
        translations[language]?.logoutError ||
          "Logout failed, but you have been logged out locally.",
      );

      // Redirect anyway
      window.location.href = "/";
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Enhanced useEffect to handle user role detection
  useEffect(() => {
    const getUserRole = () => {
      try {
        const userData = localStorage.getItem("userData");
        if (userData) {
          const user = JSON.parse(userData);
          const role = user.user_role;
          const workAssignStr = user.work_assign || "";
          const subUserId = user.sub_user_id;

          // Determine if sub-user
          const isSubUserCheck = workAssignStr && !subUserId;

          if (isSubUserCheck !== isSubUser) {
            setIsSubUser(isSubUserCheck);
            // Update isAdmin in localStorage
            localStorage.setItem("isAdmin", !isSubUserCheck);
            console.log(
              "User type updated:",
              !isSubUserCheck ? "Main Admin" : "Sub User",
            );
          }

          if (role && role !== userRole) {
            setUserRole(role);
          }

          // Parse work assignments
          if (workAssignStr) {
            const assignIds = workAssignStr
              .split(",")
              .map((id) => parseInt(id.trim()))
              .filter((id) => !isNaN(id));
            if (JSON.stringify(assignIds) !== JSON.stringify(workAssignments)) {
              console.log("Updating work assignments to:", assignIds);
              setWorkAssignments(assignIds);
            }
          } else {
            if (workAssignments.length > 0) {
              setWorkAssignments([]);
            }
          }
        } else {
          if (userRole !== null) setUserRole(null);
          if (workAssignments.length > 0) setWorkAssignments([]);
          if (isSubUser) {
            setIsSubUser(false);
            localStorage.setItem("isAdmin", true);
          }
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    };

    getUserRole();
    const handleStorageChange = (e) => {
      if (e.key === "userData") {
        getUserRole();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(getUserRole, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [userRole, workAssignments, isSubUser]);

  useEffect(() => {
    const handleCategoryChange = () => {
      const category = localStorage.getItem("project_category") || "General";
      // console.log('🔄 Sidebar category change detected:', category);

      if (category !== projectCategory) {
        // setProjectCategory(category);
        // console.log('✅ Sidebar category updated:', projectCategory, '→', category);
      }
    };

    // Listen for changes from other tabs
    window.addEventListener("storage", handleCategoryChange);

    // ✅ Listen for changes from same tab
    window.addEventListener("projectCategoryChanged", handleCategoryChange);

    // Polling as fallback
    const interval = setInterval(handleCategoryChange, 500);

    // Initial load
    handleCategoryChange();

    return () => {
      window.removeEventListener("storage", handleCategoryChange);
      window.removeEventListener(
        "projectCategoryChanged",
        handleCategoryChange,
      );
      clearInterval(interval);
    };
  }, [projectCategory]); // ✅ Add dependency to trigger re-render

  const hasWorkAssignment = (menuItemId) => {
    // If main user (not sub-user), show all items
    if (!isSubUser) {
      return true;
    }

    // If no work assignments, hide for sub-users
    if (!Array.isArray(workAssignments) || workAssignments.length === 0) {
      console.log("Sub-user with no work assignments");
      return false;
    }

    // Check if any work assignment includes this menu item
    const hasAccess = workAssignments.some((assignId) => {
      const workAssignment = WORK_ASSIGNMENTS[assignId];
      if (workAssignment && workAssignment.menu_ids.includes(menuItemId)) {
        console.log(
          `Access granted: ${menuItemId} via assignment ${assignId} (${workAssignment.work_name})`,
        );
        return true;
      }
      return false;
    });

    return hasAccess;
  };

  // Custom CSS to hide scrollbars
  const noScrollbarStyle = {
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
    WebkitOverflowScrolling: "touch",
  };

  // Custom CSS class - Modified to show scrollbar for projects section
  const hideScrollbar = `
    .no-scrollbar::-webkit-scrollbar {
      display: none;
    }
    
    /* Show scrollbar for projects section */
    .show-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    
    .show-scrollbar::-webkit-scrollbar-track {
      background: rgba(59, 130, 246, 0.1);
      border-radius: 3px;
    }
    
    .show-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.7);
      border-radius: 3px;
    }
    
    .show-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.9);
    }
    
    .show-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: rgba(255, 255, 255, 0.7) rgba(0, 98, 255, 0.1);
    }
    
    @keyframes slideDown {
      from { max-height: 0; opacity: 0; }
      to { max-height: 500px; opacity: 1; }
    }
    
    @keyframes slideUp {
      from { max-height: 500px; opacity: 1; }
      to { max-height: 0; opacity: 0; }
    }
    
    .slide-down {
      animation: slideDown 0.3s ease-out forwards;
      overflow: hidden;
    }
    
    .slide-up {
      animation: slideUp 0.3s ease-out forwards;
      overflow: hidden;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    .animate-spin {
      animation: spin 1s linear infinite;
    }
  `;

  // Function to filter admin items based on user role
  const getAdministrationItems = () => {
    if (isSubUser) {
      return [];
    }
    const allAdminItems = [
      // { id: 'role-management', icon: <Shield className="h-5 w-5" />, label: translations[language].roleManagement, component: "RoleManagement", color: 'from-blue-600 to-gray-900' },
      {
        id: "user-management",
        icon: <UserCog className="h-5 w-5" />,
        label: translations[language].userManagement,
        parent: translations[language].admin,
        component: "UserManagement",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "UserAndRoleManagement",
        icon: <UserCog className="h-5 w-5" />,
        label: translations[language].subUserManagement,
        parent: translations[language].admin,
        component: "UserAndRoleManagement",
        color: "from-blue-600 to-gray-900",
      },
    ];

    // Only show admin items if user role is "Samanvay Officer"
    if (userRole === 6) {
      return allAdminItems;
    }

    // Roles 1, 3, and 18 can only see UserManagement and UserAndRoleManagement
    if ([1, 3, 5, 7, 15, 18].includes(userRole)) {
      return allAdminItems.filter(
        (item) => item.id === "UserAndRoleManagement",
      );
    }
    // Return empty array for other roles
    return [];
  };
  const getProjectItems = () => {
    const projectItems = [
      {
        id: "project-management",
        icon: <FolderPlus className="h-5 w-5" />,
        label: translations[language].projectManagement,
        component: "ProjectManagement",
        color: "from-blue-600 to-gray-900",
        tooltip: "Create/Edit/View Project",
      },
    ];

    if (userRole === 6) {
      return projectItems.filter((item) =>
        ["project-management"].includes(item.id),
      );
    }
    return [];
  };
  const getProposalItems = () => {
    const allProjectItems = [
      {
        id: "project-management",
        icon: <FolderPlus className="h-5 w-5" />,
        label: translations[language].projectManagement,
        component: "ProjectManagement",
        color: "from-blue-600 to-gray-900",
        tooltip: "Create/Edit/View Project",
      },
      {
        id: "Proposal",
        icon: <HandHelping className="h-5 w-5" />,
        label: translations[language].proposalManagement,
        component: "Proposal",
        color: "from-blue-600 to-gray-900",
        tooltip: "Create/Edit/View Proposal",
      },
      // { id: 'landholding-list', icon: <LandPlot className="h-5 w-5" />, label: translations[language].landholdingList, component: "LandholdingList", color: 'from-blue-600 to-gray-900', tooltip: 'Add Land Holding Details' },
      {
        id: "DirectPayment",
        icon: <Wallet className="h-5 w-5" />,
        label: translations[language].DirectPurchase,
        component: "DirectPayment",
        color: "from-blue-600 to-gray-900",
        tooltip: "Direct Payment",
      },
      {
        id: "publish-proposal",
        icon: <FileText className="h-5 w-5" />,
        label:
          translations[language].ProposalPublicationPreliminaryNotification,
        component: "PublishProposal",
        color: "from-blue-600 to-gray-900",
        tooltip: "Prathmik Adhisuchna",
      },
      {
        id: "FundRequestReceived",
        icon: <IndianRupee className="h-5 w-5" />,
        label: translations[language].FundRequestReceived,
        component: "FundRequestReceived",
        color: "from-blue-600 to-gray-900",
        tooltip: "Fund Request Received",
      },
      {
        id: "JointMesurmentList",
        icon: <MapPinned className="h-5 w-5" />,
        label: translations[language].JointMeasurementDetails,
        component: "JointMesurmentList",
        color: "from-blue-600 to-gray-900",
        tooltip: "Joint Measurement Details",
      },
      // { id: 'FamilySurveyForm', icon: <UsersRound className="h-5 w-5" />, label: translations[language].FamilySurveyForm, component: 'FamilySurveyForm', color: 'from-blue-600 to-gray-900', tooltip: 'Create/Edit/View Family Survey Form' },
      {
        id: "AdvancePayment",
        icon: <Wallet className="h-5 w-5" />,
        label: translations[language].AdvancePayment,
        component: "AdvancePayment",
        color: "from-blue-600 to-gray-900",
        tooltip: "Advance Payment",
      },
      {
        id: "ObjectionList",
        icon: <MessageSquareWarning className="h-5 w-5" />,
        label: translations[language].objectionList,
        component: "ObjectionList",
        color: "from-blue-600 to-gray-900",
        tooltip: "Objection List",
      },
      {
        id: "ExtendedDate",
        icon: <CalendarClock className="h-5 w-5" />,
        label: translations[language].ExtendedDate11,
        component: "ExtendedDate",
        color: "from-blue-600 to-gray-900",
        tooltip: "Extended Date",
      },
      {
        id: "Section19",
        icon: <Scale className="h-5 w-5" />,
        label: translations[language].SideBarSection19,
        component: "Section19",
        color: "from-blue-600 to-gray-900",
        tooltip: "Section 19 Details",
      },
      {
        id: "Section21",
        icon: <Gavel className="h-5 w-5" />,
        label: translations[language].Section21,
        component: "Section21",
        color: "from-blue-600 to-gray-900",
        tooltip: "Section 21 Details",
      },
      {
        id: "Section21_1_4",
        icon: <Gavel className="h-5 w-5" />,
        label: translations[language].SidebarSection21_1_4,
        component: "Section21_1_4",
        color: "from-blue-600 to-gray-900",
        tooltip: "Section 21_1_4 Details",
      },
      {
        id: "MarketValue",
        icon: <TrendingUp className="h-5 w-5" />,
        label: translations[language].MarketValue,
        component: "MarketValue",
        color: "from-blue-600 to-gray-900",
        tooltip: "Market Value Details",
      },
      {
        id: "ConcernValuer",
        icon: <UserCheck className="h-5 w-5" />,
        label: translations[language].ConcernValuer,
        component: "ConcernValuer",
        color: "from-blue-600 to-gray-900",
        tooltip: "Concern Valuer Details",
      },
      {
        id: "Section93",
        icon: <Layers className="h-5 w-5" />,
        label: translations[language].Section93,
        component: "Section93",
        color: "from-blue-600 to-gray-900",
        tooltip: "Section 93 Details",
      },
      {
        id: "ExtendedDate19",
        icon: <CalendarClock className="h-5 w-5" />,
        label: translations[language].ExtendedDate19,
        component: "ExtendedDate19",
        color: "from-blue-600 to-gray-900",
        tooltip: "Extended Date 19 Details",
      },
      {
        id: "SelectFormat",
        icon: <ClipboardList className="h-5 w-5" />,
        label: translations[language].selectFormat,
        component: "SelectFormat",
        color: "from-blue-600 to-gray-900",
        tooltip: "Select Format Details",
      },
      {
        id: "Final-Award",
        icon: <Award className="h-5 w-5" />,
        label: translations[language].finalaward,
        component: "FinalReward",
        color: "from-blue-600 to-gray-900",
        tooltip: "Final Award Details",
      },
      {
        id: "WorkersJudgment",
        icon: <Gavel className="h-5 w-5" />,
        label: translations[language].Workersjudgment,
        component: "WorkersJudgment",
        color: "from-blue-600 to-gray-900",
        tooltip: "Workers Judgment Details",
      },
      {
        id: "Payment",
        icon: <IndianRupee className="h-5 w-5" />,
        label: translations[language].Payment,
        component: "Payment",
        color: "from-blue-600 to-gray-900",
        tooltip: "Payment Details",
      },
      {
        id: "PAP",
        icon: <IdCard className="h-5 w-5" />,
        label: translations[language].ProjectAffectedPerson,
        component: "PAP",
        color: "from-blue-600 to-gray-900",
        tooltip: "Project Affected Person Details",
      },
      // { id: 'Kalam11', icon: <FileSearch className="h-5 w-5" />, label: translations[language].kalam11, component: "Kalam11", color: 'from-blue-600 to-gray-900', tooltip: 'Kalam 11 Details' },
      // { id: 'Namuna_A', icon: <FileCheck className="h-5 w-5" />, label: translations[language].namunaA, component: "Namuna_A", color: 'from-blue-600 to-gray-900', tooltip: 'Namuna A Details' },
      // { id: 'Namuna_B', icon: <FileBadge2 className="h-5 w-5" />, label: translations[language].namunaB, component: "Namuna_B", color: 'from-blue-600 to-gray-900', tooltip: 'Namuna B Details' },
      // { id: 'TabaPautiPunarvarshan', icon: <Landmark className="h-5 w-5" />, label: translations[language].tabaPautiPunarvarshan, component: "TabaPautiPunarvarshan", color: 'from-blue-600 to-gray-900', tooltip: 'Taba Pauti Punarvarshan Details' },
      // { id: 'Namuna_K', icon: <FileSpreadsheet className="h-5 w-5" />, label: translations[language].namunaK, component: "Namuna_K", color: 'from-blue-600 to-gray-900', tooltip: 'Namuna K Details' },
      // { id: 'LandAllocationReport', icon: <Building2 className="h-5 w-5" />, label: translations[language].landAllocationReport, component: "LandAllocationReport", color: 'from-blue-600 to-gray-900', tooltip: 'Land Allocation Report Details' },
      // { id: 'FamilySurveyForm', icon: <UsersRound className="h-5 w-5" />, label: translations[language].FamilySurveyForm, component: 'FamilySurveyForm', color: 'from-blue-600 to-gray-900', },
      // { id: 'LandBeneficiaryList', icon: <UsersRound className="h-5 w-5" />, label: translations[language].landBeneficiaryList, component: "LandBeneficiaryList", color: 'from-blue-600 to-gray-900', tooltip: 'Land Beneficiary List Details' },
      // { id: 'LandBeneficiaryLetter', icon: <Layers className="h-5 w-5" />, label: translations[language].landBeneficiaryLetter, component: "LandBeneficiaryLetter", color: 'from-blue-600 to-gray-900' },
      // { id: 'LandObjectionRegister', icon: <ClipboardList className="h-5 w-5" />, label: translations[language].landObjectionRegister, component: "LandObjectionRegister", color: 'from-blue-600 to-gray-900', tooltip: 'Land Objection Register Details' },
      // { id: 'FinalLandAllotmentList', icon: <FileBadge2 className="h-5 w-5" />, label: translations[language].finalLandAllotmentList, component: "FinalLandAllotmentList", color: 'from-blue-600 to-gray-900' },
      // { id: 'FinalLandAllotmentLetter', icon: <FileText className="h-5 w-5" />, label: translations[language].finalLandAllotmentLetter, component: "FinalLandAllotmentLetter", color: 'from-blue-600 to-gray-900', tooltip: 'Final Land Allotment Letter Details' },
      // { id: 'Punavarshan', icon: <Gavel className="h-5 w-5" />, label: translations[language].Punavarshan, component: "Punavarshan", color: 'from-blue-600 to-gray-900', tooltip: 'Punavarshan Details' },
      {
        id: "SearchRecords",
        icon: <Search className="h-5 w-5" />,
        label: translations[language].searchRecords,
        component: "SearchRecords",
        color: "from-blue-600 to-gray-900",
        tooltip: "Search Records Details",
      },
      {
        id: "Tehsildar",
        icon: <Landmark className="h-5 w-5" />,
        label: translations[language].Tehsilar,
        component: "Tehsildar",
        color: "from-blue-600 to-gray-900",
        tooltip: "Tehsildar Details",
      },
      {
        id: "Notification",
        icon: <Gavel className="h-5 w-5" />,
        label: "Notification",
        component: "Notification",
        color: "from-blue-600 to-gray-900",
        tooltip: "Notification Details",
      },
      // { id: 'PublicationSections', icon: <ClipboardList className="h-5 w-5" />, label: "Completed Legacy", component: "PublicationSection", color: 'from-blue-600 to-gray-900', tooltip: 'Completed Legacy Award' },
      // { id: 'ViewDocument', icon: <Search className="h-5 w-5" />, label: translations[language].publicDocument, component: "ViewDocument", color: 'from-blue-600 to-gray-900', tooltip: 'View All Documents Details' },

      //For Railway
      {
        id: "RailwayProposal",
        icon: <HandHelping className="h-5 w-5" />,
        label: translations[language].railwayProposal,
        component: "RailwayProposal",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "RailwayLandHoldingList",
        icon: <FileSpreadsheet className="h-5 w-5" />,
        label: translations[language].railwayLandHoldingList,
        component: "RailwayLandHoldingList",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "RailwayPublishProposal",
        icon: <FileBadge2 className="h-5 w-5" />,
        label: translations[language].railwayPublishProposal,
        component: "RailwayPublishProposal",
        color: "from-blue-600 to-gray-900",
      },
      // { id: 'RailwayFundRequestReceived', icon: <FileBadge2 className="h-5 w-5" />, label: translations[language].railwayFundRequest, component: "RailwayFundRequestReceived", color: 'from-blue-600 to-gray-900' },
      {
        id: "RailwayJointMesurmentList",
        icon: <Ruler className="h-5 w-5" />,
        label: translations[language].railwayJointMesurmentList,
        component: "RailwayJointMesurmentList",
        color: "from-blue-600 to-gray-900",
      },
      // { id: 'RailwayAdvancePayment', icon: <Search className="h-5 w-5" />, label: translations[language].railwayAdvancePayment, component: "RailwayAdvancePayment", color: 'from-blue-600 to-gray-900' },
      {
        id: "RailwayObjectionList",
        icon: <MessageSquareWarning className="h-5 w-5" />,
        label: translations[language].railwayObjectionList,
        component: "RailwayObjectionList",
        color: "from-blue-600 to-gray-900",
      },
      // { id: 'RailwayExtendedDate', icon: <CalendarClock className="h-5 w-5" />, label: translations[language].railwayExtendedDate, component: "RailwayExtendedDate", color: 'from-blue-600 to-gray-900' },
      // { id: 'RailwaySection19', icon: <CalendarClock className="h-5 w-5" />, label: translations[language].railwaySection19, component: "RailwaySection19", color: 'from-blue-600 to-gray-900' },
      // { id: 'RailwaySection21', icon: <CalendarClock className="h-5 w-5" />, label: translations[language].railwaySection21, component: "RailwaySection21", color: 'from-blue-600 to-gray-900' },
      // { id: 'RailwayMarketValue', icon: <CalendarClock className="h-5 w-5" />, label: translations[language].railwayMarketValue, component: "RailwayMarketValue", color: 'from-blue-600 to-gray-900' },
      // { id: 'RailwaySection93', icon: <CalendarClock className="h-5 w-5" />, label: translations[language].railwaySection93, component: "RailwaySection93", color: 'from-blue-600 to-gray-900' },
      // { id: 'RailwayExtendedDate19', icon: <CalendarClock className="h-5 w-5" />, label: translations[language].railwayExtendedDate19, component: "RailwayExtendedDate19", color: 'from-blue-600 to-gray-900' },
      {
        id: "RailwaySelectFormat",
        icon: <LayoutTemplate className="h-5 w-5" />,
        label: translations[language].railwaySelectFormat,
        component: "RailwaySelectFormat",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "RailwayFinalReward",
        icon: <Award className="h-5 w-5" />,
        label: translations[language].railwayFinalReward,
        component: "RailwayFinalReward",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "RailwayWorkersJudgment",
        icon: <Gavel className="h-5 w-5" />,
        label: translations[language].railwayWorkersJudgment,
        component: "RailwayWorkersJudgment",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "RailwayPayment",
        icon: <CreditCard className="h-5 w-5" />,
        label: translations[language].railwayPayment,
        component: "RailwayPayment",
        color: "from-blue-600 to-gray-900",
      },
      // { id: 'RailwayPAP', icon: <CalendarClock className="h-5 w-5" />, label: translations[language].railwayPAP, component: "RailwayPAP", color: 'from-blue-600 to-gray-900' },
      {
        id: "RailwayPunavarshan",
        icon: <CalendarClock className="h-5 w-5" />,
        label: translations[language].railwayPunavarshan,
        component: "RailwayPunavarshan",
        color: "from-blue-600 to-gray-900",
      },
      // { id: 'RailwaySearchBar', icon: <CalendarClock className="h-5 w-5" />, label: translations[language].railwaySearchBar, component: "RailwaySearchBar", color: 'from-blue-600 to-gray-900' },
      // { id: 'Section20A', icon: <Search className="h-5 w-5" />, label: translations[language].section20A, component: "Section20A", color: 'from-blue-600 to-gray-900' },
      // { id: 'Section20B', icon: <Search className="h-5 w-5" />, label: translations[language].section20B, component: "Section20B", color: 'from-blue-600 to-gray-900' },
      // { id: 'Section20D', icon: <Search className="h-5 w-5" />, label: translations[language].section20D, component: "Section20D", color: 'from-blue-600 to-gray-900' },
      // { id: 'Section20E', icon: <Search className="h-5 w-5" />, label: translations[language].section20E, component: "Section20E", color: 'from-blue-600 to-gray-900' },
      // { id: 'Section20F', icon: <Search className="h-5 w-5" />, label: translations[language].section20F, component: "Section20F", color: 'from-blue-600 to-gray-900' },
      // { id: 'Section20G', icon: <Search className="h-5 w-5" />, label: translations[language].section20G, component: "Section20G", color: 'from-blue-600 to-gray-900' },
      // { id: 'Section20I', icon: <Search className="h-5 w-5" />, label: translations[language].section20I, component: "Section20I", color: 'from-blue-600 to-gray-900' },
      // { id: 'Section20F_6', icon: <Search className="h-5 w-5" />, label: translations[language].section20F_6, component: "Section20F(6)", color: 'from-blue-600 to-gray-900' },
      // { id: 'Railway_AppealLies', icon: <Search className="h-5 w-5" />, label: translations[language].appealLies, component: "AppealLies", color: 'from-blue-600 to-gray-900' },

      //For Highway
      {
        id: "HighwayProposal",
        icon: <HandHelping className="h-5 w-5" />,
        label: translations[language].highwayProposal,
        component: "HighwayProposal",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayLandHoldingList",
        icon: <FileBadge2 className="h-5 w-5" />,
        label: translations[language].highwayLandHoldingList,
        component: "HighwayLandHoldingList",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayPublishProposal",
        icon: <Layers className="h-5 w-5" />,
        label:
          translations[language].ProposalPublicationPreliminaryNotification,
        component: "HighwayPublishProposal",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "Cala",
        icon: <FolderPlus className="h-5 w-5" />,
        label: translations[language].cala,
        component: "Cala",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "SideBarSection3A",
        icon: <Search className="h-5 w-5" />,
        label: translations[language].section3A_Eng,
        component: "SideBarSection3A",
        color: "from-blue-600 to-gray-900",
      },
      // { id: 'HighwayFundRequestReceived', icon: <IndianRupee className="h-5 w-5" />, label: translations[language].highwayFundRequestReceived, component: "HighwayFundRequestReceived", color: 'from-blue-600 to-gray-900' },
      {
        id: "HighwayJointMesurmentList",
        icon: <LandPlot className="h-5 w-5" />,
        label: translations[language].highwayJointMesurmentList,
        component: "HighwayJointMesurmentList",
        color: "from-blue-600 to-gray-900",
      },
      // { id: 'HighwayAdvancePayment', icon: <IndianRupee className="h-5 w-5" />, label: translations[language].highwayAdvancePayment, component: "HighwayAdvancePayment", color: 'from-blue-600 to-gray-900' },
      {
        id: "HighwayObjectionList",
        icon: <Clock className="h-5 w-5" />,
        label: translations[language].highwayobjectionList,
        component: "HighwayObjectionList",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayExtendedDate",
        icon: <CalendarClock className="h-5 w-5" />,
        label: translations[language].ExtendedDate11,
        component: "HighwayExtendedDate",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwaySection19",
        icon: <Clock className="h-5 w-5" />,
        label: translations[language].highwayTehsildar,
        component: "HighwaySection19",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwaySection21",
        icon: <Clock className="h-5 w-5" />,
        label: translations[language].highwaySection21,
        component: "HighwaySection21",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwaySection21_1_4",
        icon: <Clock className="h-5 w-5" />,
        label: translations[language].highwaySidebarSection21_1_4,
        component: "HighwaySection21_1_4",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayMarketValue",
        icon: <Layers className="h-5 w-5" />,
        label: translations[language].highwayMarketValue,
        component: "HighwayMarketValue",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayConcernValuer",
        icon: <Users className="h-5 w-5" />,
        label: translations[language].highwayConcernValuer,
        component: "HighwayConcernValuer",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwaySection93",
        icon: <Layers className="h-5 w-5" />,
        label: translations[language].highwaySection93,
        component: "HighwaySection93",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayExtendedDate19",
        icon: <CalendarClock className="h-5 w-5" />,
        label: translations[language].highwayExtendedDate19,
        component: "HighwayExtendedDate19",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwaySelectFormat",
        icon: <FileBadge2 className="h-5 w-5" />,
        label: translations[language].highwaySelectFormat,
        component: "HighwaySelectFormat",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayFinal-Award",
        icon: <FileBadge2 className="h-5 w-5" />,
        label: translations[language].highwayfinalaward,
        component: "HighwayFinalReward",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayWorkersJudgment",
        icon: <FileBadge2 className="h-5 w-5" />,
        label: translations[language].highwayWorkersjudgment,
        component: "HighwayWorkersJudgment",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayPayment",
        icon: <IndianRupee className="h-5 w-5" />,
        label: translations[language].highwayPayment,
        component: "HighwayPayment",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayPAP",
        icon: <IdCard className="h-5 w-5" />,
        label: translations[language].highwayProjectAffectedPerson,
        component: "HighwayPAP",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayPunavarshan",
        icon: <Search className="h-5 w-5" />,
        label: translations[language].highwayPunavarshan,
        component: "HighwayPunavarshan",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwaySearchBar",
        icon: <Search className="h-5 w-5" />,
        label: translations[language].highwaySearchBar,
        component: "HighwaySearchBar",
        color: "from-blue-600 to-gray-900",
      },
      {
        id: "HighwayTehsildar",
        icon: <Search className="h-5 w-5" />,
        label: translations[language].highwayTehsilar,
        component: "HighwayTehsildar",
        color: "from-blue-600 to-gray-900",
      },
    ];
    if (projectCategory === "Railway") {
      return allProjectItems.filter((item) =>
        [
          "project-management",
          "RailwayProposal",
          "RailwayLandHoldingList",
          "RailwayPublishProposal",
          "RailwayJointMesurmentList",
          "RailwayFundRequestReceived",
          "RailwayAdvancePayment",
          "RailwayObjectionList",
          "RailwayExtendedDate",
          "RailwaySection19",
          "RailwaySection21",
          "RailwayMarketValue",
          "RailwaySection93",
          "RailwayExtendedDate19",
          "RailwaySelectFormat",
          "RailwayFinalReward",
          "RailwayWorkersJudgment",
          "RailwayPayment",
          "RailwayPAP",
          "RailwayPunavarshan",
          "RailwaySearchBar",
          "Section20A",
          "Section20B",
          "Section20C",
          "Section20D",
          "Section20E",
          "Section20F",
          "Section20G",
          "Section20I",
          "Section20F_6",
          "Railway_AppealLies",
        ].includes(item.id),
      );
    }

    if (projectCategory === "Highway") {
      return allProjectItems.filter((item) =>
        [
          "project-management",
          "HighwayProposal",
          "HighwayLandHoldingList",
          "HighwayPublishProposal",
          "Cala",
          "SideBarSection3A",
          "HighwayFundRequestReceived",
          "HighwayJointMesurmentList",
          "HighwayAdvancePayment",
          "HighwayObjectionList",
          "HighwayExtendedDate",
          "HighwaySection19",
          "HighwaySection21",
          "HighwayMarketValue",
          "HighwayConcernValuer",
          "HighwaySection93",
          "HighwayExtendedDate19",
          "HighwaySelectFormat",
          "HighwayFinal-Award",
          "HighwayWorkersJudgment",
          "HighwayPayment",
          "HighwayPAP",
          "HighwayPunavarshan",
          "HighwaySearchBar",
          "HighwayTehsildar",
          "Section3B",
          "Section3C",
          "Section3D",
          "Section3G",
          "Section3H",
          "TabaPauti",
          "Highway_AppealLies",
        ].includes(item.id),
      );
    }

    // Filter items based on user role
    let filteredItems = [];

    switch (userRole) {
      case 3: // LAO
        filteredItems = allProjectItems.filter((item) =>
          [
            "Proposal",
            "landholding-list",
            "publish-proposal",
            "FundRequestReceived",
            "ObjectionList",
            "ExtendedDate",
            "ExtendedDate19",
            "Section19",
            "Section21",
            "compensation",
            "PAP",
            "Section93",
            "SelectFormat",
            "Final-Award",
            "WorkersJudgment",
            "KabjaPauti",
            "Payment",
            "JointMesurmentList",
            "MarketValue",
            "AdvancePayment",
            "Notification",
            "ViewDocument",
            "PublicationSections",
            "SearchRecords",
          ].includes(item.id),
        );
        break;
      case 5: // Tehsildar
        filteredItems = allProjectItems.filter((item) =>
          ["LandBeneficiaryList", , "Tehsildar", "Final-Award"].includes(
            item.id,
          ),
        );
        break;
      case 7: // Surveyor
        filteredItems = allProjectItems.filter((item) =>
          ["JointMesurmentList", "FamilySurveyForm", "Final-Award"].includes(
            item.id,
          ),
        );
        break;
      case 15: // Document Verification Officer
        filteredItems = allProjectItems.filter((item) =>
          ["landholding-list", "JointMesurmentList", "Final-Award"].includes(
            item.id,
          ),
        );
        break;
      case 21: // AgricultureAgency
        filteredItems = allProjectItems.filter(
          (item) => item.id === "ConcernValuer",
        );
        break;
      // case 22: // DRO
      //     const droItemIds = ['JointMesurmentList', 'FamilySurveyForm', 'Punavarshan', 'Kalam11',
      //         'LandBeneficiaryList', 'LandObjectionRegister',
      //         'FinalLandAllotmentList', 'FinalLandAllotmentLetter', 'Namuna_A',
      //         'Namuna_B', 'TabaPautiPunarvarshan', 'Namuna_K', 'LandAllocationReport',];

      //     // Filter items
      //     const droItems = allProjectItems.filter(item => droItemIds.includes(item.id));

      //     // Sort items according to the desired sequence
      //     filteredItems = droItemIds
      //         .map(id => droItems.find(item => item.id === id))
      //         .filter(item => item !== undefined);
      //     break;
      case 23: // TP
        filteredItems = allProjectItems.filter(
          (item) => item.id === "ConcernValuer",
        );
        break;
      case 24: // Forest
        filteredItems = allProjectItems.filter(
          (item) => item.id === "ConcernValuer",
        );
        break;
      case 18: // Project Incharge
        filteredItems = allProjectItems.filter((item) =>
          [
            "Proposal",
            "landholding-list",
            "acquisition-records",
            "PublishProposal",
            "JointMesurmentList",
            "FundRequestReceived",
            "Namuna_A",
          ].includes(item.id),
        );
        break;
      case 6: // Samanvay
        filteredItems = allProjectItems.filter((item) =>
          [
            "Proposal",
            "landholding-list",
            "acquisition-records",
            "publish-proposal",
            "FundRequestReceived",
            "ObjectionList",
            "ExtendedDate",
            "ExtendedDate19",
            "Section19",
            "Section21",
            "compensation",
            "PAP",
            "Section93",
            "SelectFormat",
            "Final-Award",
            "WorkersJudgment",
            "KabjaPauti",
            "Payment",
            "JointMesurmentList",
            "MarketValue",
            "AdvancePayment",
            "ViewDocument",
            "PublicationSections",
            "SearchRecords",
          ].includes(item.id),
        );
        break;
      case 1: // Collector
        filteredItems = allProjectItems.filter((item) =>
          [
            "Proposal",
            "landholding-list",
            "acquisition-records",
            "publish-proposal",
            "FundRequestReceived",
            "ObjectionList",
            "ExtendedDate",
            "ExtendedDate19",
            "Section19",
            "Section21",
            "compensation",
            "PAP",
            "Section93",
            "SelectFormat",
            "Final-Award",
            "WorkersJudgment",
            "KabjaPauti",
            "Payment",
            "JointMesurmentList",
            "FamilySurveyForm",
            "Kalam11",
            "LandBeneficiaryList",
            "LandObjectionRegister",
            "FinalLandAllotmentList",
            "FinalLandAllotmentLetter",
            "Namuna_A",
            "Namuna_B",
            "Namuna_K",
            "TabaPautiPunarvarshan",
            "LandAllocationReport",
            "MarketValue",
            "AdvancePayment",
            "ViewDocument",
            "PublicationSections",
            "SearchRecords",
          ].includes(item.id),
        );
        break;

      case 14: // Data entry
        filteredItems = allProjectItems.filter((item) =>
          [
            "Proposal",
            "landholding-list",
            "acquisition-records",
            "publish-proposal",
            "FundRequestReceived",
            "ObjectionList",
            "ExtendedDate",
            "ExtendedDate19",
            "Section19",
            "Section21",
            "compensation",
            "ConcernValuer",
            "PAP",
            "Section93",
            "SelectFormat",
            "Final-Award",
            "WorkersJudgment",
            "KabjaPauti",
            "Payment",
            "JointMesurmentList",
            "MarketValue",
            "AdvancePayment",
            "Tehsildar",
            "FamilySurveyForm",
            "ViewDocument",
            "PublicationSections",
          ].includes(item.id),
        );
        break;
      default:
        filteredItems = [];
    }

    // ADDITIONAL FILTER: Apply work assignment filter
    if (workAssignments && workAssignments.length > 0) {
      // console.log('Applying work assignment filter. Current filtered items:', filteredItems.map(i => i.id));
      // console.log('Work assignments:', workAssignments);

      const beforeFilterCount = filteredItems.length;
      filteredItems = filteredItems.filter(
        (item) => hasWorkAssignment(item.id),
        // console.log(`Item ${item.id}: hasAccess = ${hasAccess}`);
        // return hasAccess;
      );

      // console.log(`Filtered from ${beforeFilterCount} to ${filteredItems.length} items`);
      // console.log('Final items:', filteredItems.map(i => i.id));
    } else {
      // console.log('No work assignment filter applied. Showing all role-based items.');
    }

    return filteredItems;
  };

  const getPunarvarshanItems = () => {
    const allPunarvarshanItems = [
      {
        id: "JointMesurmentList",
        icon: <MapPinned className="h-5 w-5" />,
        label: translations[language].JointMeasurementDetails,
        component: "JointMesurmentList",
        color: "from-blue-600 to-gray-900",
        tooltip: "Joint Measurement Details",
      },
      {
        id: "FamilySurveyForm",
        icon: <UsersRound className="h-5 w-5" />,
        label: translations[language].FamilySurveyForm,
        component: "FamilySurveyForm",
        color: "from-blue-600 to-gray-900",
        tooltip: "Create/Edit/View Family Survey Form",
      },
      {
        id: "Punavarshan",
        icon: <Gavel className="h-5 w-5" />,
        label: translations[language].Punavarshan,
        component: "Punavarshan",
        color: "from-blue-600 to-gray-900",
        tooltip: "Punavarshan Details",
      },
      {
        id: "Kalam11",
        icon: <FileSearch className="h-5 w-5" />,
        label: translations[language].kalam11,
        component: "Kalam11",
        color: "from-blue-600 to-gray-900",
        tooltip: "Kalam 11 Details",
      },
      {
        id: "LandBeneficiaryList",
        icon: <UsersRound className="h-5 w-5" />,
        label: translations[language].landBeneficiaryList,
        component: "LandBeneficiaryList",
        color: "from-blue-600 to-gray-900",
        tooltip: "Land Beneficiary List Details",
      },
      {
        id: "LandObjectionRegister",
        icon: <ClipboardList className="h-5 w-5" />,
        label: translations[language].landObjectionRegister,
        component: "LandObjectionRegister",
        color: "from-blue-600 to-gray-900",
        tooltip: "Land Objection Register Details",
      },
      {
        id: "FinalLandAllotmentLetter",
        icon: <FileText className="h-5 w-5" />,
        label: translations[language].finalLandAllotmentLetter,
        component: "FinalLandAllotmentLetter",
        color: "from-blue-600 to-gray-900",
        tooltip: "Final Land Allotment Letter Details",
      },
      {
        id: "Namuna_A",
        icon: <FileCheck className="h-5 w-5" />,
        label: translations[language].namunaA,
        component: "Namuna_A",
        color: "from-blue-600 to-gray-900",
        tooltip: "Namuna A Details",
      },
      {
        id: "Namuna_B",
        icon: <FileBadge2 className="h-5 w-5" />,
        label: translations[language].namunaB,
        component: "Namuna_B",
        color: "from-blue-600 to-gray-900",
        tooltip: "Namuna B Details",
      },
      {
        id: "Namuna_K",
        icon: <FileSpreadsheet className="h-5 w-5" />,
        label: translations[language].namunaK,
        component: "Namuna_K",
        color: "from-blue-600 to-gray-900",
        tooltip: "Namuna K Details",
      },
      {
        id: "TabaPautiPunarvarshan",
        icon: <Landmark className="h-5 w-5" />,
        label: translations[language].tabaPautiPunarvarshan,
        component: "TabaPautiPunarvarshan",
        color: "from-blue-600 to-gray-900",
        tooltip: "Taba Pauti Punarvarshan Details",
      },
      {
        id: "LandAllocationReport",
        icon: <Building2 className="h-5 w-5" />,
        label: translations[language].landAllocationReport,
        component: "LandAllocationReport",
        color: "from-blue-600 to-gray-900",
        tooltip: "Land Allocation Report Details",
      },
      {
        id: "RehabPayment",
        icon: <Building2 className="h-5 w-5" />,
        label: "Rehab Payment",
        component: "RehabPayment",
        color: "from-blue-600 to-gray-900",
        tooltip: "Rehab Payment",
      },
    ];

    // Collector and Samanvay — show all items
    if ([1, 6, 22].includes(userRole)) {
      return allPunarvarshanItems;
    }

    if (userRole === 18) {
      return allPunarvarshanItems.filter((item) =>
        ["Namuna_A","FamilySurveyForm"].includes(item.id),
      );
    }

    if (userRole === 7) {
      return allPunarvarshanItems.filter((item) =>
        ["FamilySurveyForm"].includes(item.id),
      );
    }

    // All other roles — no access
    return [];
  };

  // Function to filter Land Management items based on user role
  const getLandManagementItems = () => {
    if (isSubUser) {
      return [];
    }
    const allLandItems = [
      // { id: 'village-management', icon: <MapPin className="h-5 w-5" />, label: translations[language].villageManagement, component: "Villagemanagement", color: 'from-blue-600 to-gray-900' },
      // { id: 'type-management', icon: <GitBranch className="h-5 w-5" />, label: translations[language].typeManagement, parent: translations[language].land, component: "LandTypeManagement", color: 'from-blue-600 to-gray-900' },
      // { id: 'owner-management', icon: <Database className="h-5 w-5" />, label: translations[language].ownerManagement, parent: translations[language].land, component: "AreaUnitManagement", color: 'from-blue-600 to-gray-900' }
    ];

    // Only show Land Management items for Samanvay Officer
    if (userRole === 6) {
      return allLandItems;
    }

    return [];
  };

  const menuCategories = [
    // Special dashboard item with no dropdown
    {
      id: "dashboard",
      title: translations[language].dashboard,
      icon: <Home className="h-4 w-4" />,
      component: null, // Set the component to open
      isSpecial: true, // Flag to identify this as a special direct-click item
      color: "from-blue-500 to-blue-600",
    },
    // Conditionally include Administration section
    ...(getAdministrationItems().length > 0
      ? [
          {
            title: translations[language].administration,
            icon: <Award className="h-4 w-4" />,
            items: getAdministrationItems(),
          },
        ]
      : []),
    // Conditionally include Land Management section
    ...(getLandManagementItems().length > 0
      ? [
          {
            title: translations[language].landMaster,
            icon: <Building className="h-4 w-4" />,
            items: getLandManagementItems(),
          },
        ]
      : []),
    // Conditionally include Projects section
    ...(getProjectItems().length > 0 && userRole !== 22
      ? [
          {
            title: translations[language].projectmanagement,
            icon: <Layers className="h-4 w-4" />,
            isScrollable: true,
            items: getProjectItems(),
          },
        ]
      : []),
    // Conditionally include Projects section
    ...(getProposalItems().length > 0
      ? [
          {
            title: translations[language].ProposalManagement,
            icon: <FileText className="h-4 w-4" />,
            isScrollable: true,
            items: getProposalItems(),
          },
        ]
      : []),
    ...(getPunarvarshanItems().length > 0
      ? [
          {
            title: translations[language].PunavarshanManagement,
            icon: <HousePlus className="h-4 w-4" />,
            isScrollable: true,
            items: getPunarvarshanItems(),
          },
        ]
      : []),
    ...(!isSubUser && [1, 3, 5, 6, 7, 15, 18].includes(userRole)
      ? [
          {
            title: translations[language].completedPortel,
            icon: <FileText className="h-4 w-4" />,
            isScrollable: true,
            items: [
              {
                id: "ViewDocument",
                icon: <Search className="h-5 w-5" />,
                label: translations[language].completedPortel,
                component: "ViewDocument",
                color: "from-blue-600 to-gray-900",
                tooltip: "View All Documents Details",
              },
            ],
          },
        ]
      : []),
    // Modified Reports to be a direct-click item like Dashboard
    // {
    //     id: 'Reports',
    //     title: translations[language].reports,
    //     icon: <Shield className="h-4 w-4" />,
    //     component: "Reports", // Set to open the Reports component directly
    //     isSpecial: true, // Flag to identify this as a special direct-click item
    //     color: 'from-blue-500 to-blue-600',
    // },
  ];

  useEffect(() => {
    if (!currentComponent) {
      setActiveItem("dashboard");
    } else {
      // try to match currentComponent with menu item id or component
      const compLower = currentComponent.toString().toLowerCase();
      let matchedId = "dashboard";

      for (const category of menuCategories) {
        if (category.items) {
          for (const item of category.items) {
            const idLower = item.id.toLowerCase();
            const compNameLower = item.component?.toString().toLowerCase();
            if (idLower === compLower || compNameLower === compLower) {
              matchedId = item.id;
            }
          }
        } else if (category.id && category.id.toLowerCase() === compLower) {
          matchedId = category.id;
        }
      }

      setActiveItem(matchedId);
    }
  }, [currentComponent, menuCategories]);

  const handleItemClick = (item) => {
    setActiveItem(item.id);

    // Navigate to the selected component
    if (setSelectedComponent) {
      setSelectedComponent(item.component || null);
    }
  };

  // Function to toggle category expansion
  const toggleCategory = (title) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <div
      className={`bg-gradient-to-b from-blue-900 to-blue-800 shadow-lg h-screen flex flex-col ${collapsed ? "w-20" : "w-64"} transition-all duration-300 ease-in-out z-10`}
    >
      {/* Inject custom CSS */}
      <style>{hideScrollbar}</style>

      {/* Logo Section */}
      <div className="flex items-center justify-between p-4 border-b border-blue-700">
        {!collapsed && (
          <div className="flex items-center">
            <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-lg p-2 mr-2 text-white">
              <img
                src="/home image/buldana image.svg"
                alt="Logo"
                className="h-6 w-6 object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-sm text-white">
                "{translations[language]?.landAcquisition || "Land Acquisition"}
                "
              </span>
              <span className="text-xs text-blue-200">
                {translations[language]?.informationSystem ||
                  "Information System"}
              </span>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="w-full flex justify-center">
            <div className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-lg p-2 text-white shadow-lg shadow-amber-500/20">
              <Flag className="h-4 w-4 animate-pulse" />
            </div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 rounded-full hover:bg-blue-700 focus:outline-none transition-all duration-200 text-blue-300 hover:text-white"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Menu Sections - Hidden scrollbar */}
      <div
        className="flex-1 overflow-y-auto py-2 no-scrollbar"
        style={noScrollbarStyle}
      >
        {menuCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-3">
            {/* Special handling for direct click items (Dashboard & Reports) */}
            {category.isSpecial ? (
              <div
                onClick={() => {
                  setActiveItem(category.id);
                  if (setSelectedComponent) {
                    setSelectedComponent(category.component || null);
                  }
                }}
                onMouseEnter={() => setHoveredItem(category.id)}
                onMouseLeave={() => setHoveredItem(null)}
                className={`px-4 py-2 flex items-center cursor-pointer relative
                  ${
                    activeItem === category.id
                      ? "bg-gradient-to-r from-blue-800 to-blue-700 text-white"
                      : "text-blue-300 hover:bg-blue-800 hover:text-white"
                  } 
                  transition-all duration-200 group`}
              >
                {/* Active indicator */}
                {activeItem === category.id && (
                  <>
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-amber-500 rounded-r-full animate-pulse"></div>
                    <div className="absolute right-0 w-1/4 top-0 bottom-0 bg-gradient-to-l from-transparent to-blue-700 opacity-20"></div>
                  </>
                )}

                {/* Icon container with gradient background when active */}
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300
                  ${
                    activeItem === category.id
                      ? `bg-gradient-to-br from-amber-400 to-amber-500 shadow-lg shadow-amber-500/20`
                      : "bg-blue-800 group-hover:bg-blue-700"
                  }`}
                >
                  <div
                    className={`${activeItem === category.id ? "text-white animate-pulse" : "text-blue-300 group-hover:text-white"}`}
                  >
                    {category.icon}
                  </div>
                </div>

                {!collapsed && (
                  <div className="ml-3">
                    <span
                      className={`text-sm ${activeItem === category.id ? "font-medium" : ""}`}
                    >
                      {category.title}
                    </span>
                  </div>
                )}

                {/* Tooltip for collapsed mode */}
                {collapsed && hoveredItem === category.id && (
                  <div className="absolute left-full ml-4 px-3 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white text-xs rounded-md z-10 whitespace-nowrap shadow-xl border border-blue-700 animate-fadeIn">
                    <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-blue-900"></div>
                    <div className="font-medium bg-gradient-to-r from-amber-300 to-amber-400 bg-clip-text text-transparent">
                      {category.title}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Regular category with dropdown
              <>
                {/* Category Header - Now clickable */}
                <div
                  className={`px-4 py-2 flex items-center justify-between cursor-pointer 
                    hover:bg-blue-800 transition-colors duration-200`}
                  onClick={() => !collapsed && toggleCategory(category.title)}
                >
                  <div className="flex items-center">
                    <span className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-lg p-2 mr-2 text-white shadow-lg shadow-amber-500/20">
                      {category.icon}
                    </span>
                    {!collapsed && (
                      <h3 className="text-xs font-semibold text-blue-200 uppercase tracking-wider">
                        {category.title}
                      </h3>
                    )}
                  </div>

                  {/* Dropdown arrow indicator */}
                  {!collapsed && (
                    <div className="text-blue-300">
                      {expandedCategories[category.title] ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  )}

                  {/* Tooltip for collapsed mode */}
                  {collapsed && (
                    <div
                      className="group-hover:block hidden absolute left-full ml-4 px-3 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white text-xs rounded-md z-10 whitespace-nowrap shadow-xl border border-blue-700"
                      style={{ top: "50%", transform: "translateY(-50%)" }}
                    >
                      <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-blue-900"></div>
                      <div className="font-medium">{category.title}</div>
                    </div>
                  )}
                </div>

                {/* Category Items - With animation and conditional scrolling for Projects */}
                <div
                  className={`${expandedCategories[category.title] ? "slide-down" : "slide-up"} 
    ${!expandedCategories[category.title] && "hidden"}`}
                >
                  <div
                    className={`${category.isScrollable ? `max-h-80 overflow-y-auto ${category.isScrollable ? "show-scrollbar" : "no-scrollbar"}` : ""}`}
                    style={category.isScrollable ? {} : noScrollbarStyle}
                  >
                    {category.items.map((item, itemIndex) => (
                      <div
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        onMouseEnter={() => setHoveredItem(item.id)}
                        onMouseLeave={() => setHoveredItem(null)}
                        title={
                          !collapsed && item.tooltip ? item.tooltip : undefined
                        }
                        className={`flex items-center pl-8 pr-4 py-3 cursor-pointer relative
                        ${
                          activeItem === item.id
                            ? "bg-gradient-to-r from-blue-800 to-blue-700 text-white"
                            : "text-blue-300 hover:bg-blue-800 hover:text-white"
                        } 
                        transition-all duration-200 group`}
                        style={{
                          animationDelay: `${categoryIndex * 100 + itemIndex * 50}ms`,
                          animation: "fadeIn 0.5s ease-out forwards",
                        }}
                      >
                        {/* Active indicator */}
                        {activeItem === item.id && (
                          <>
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-amber-500 rounded-r-full animate-pulse"></div>
                            <div className="absolute right-0 w-1/4 top-0 bottom-0 bg-gradient-to-l from-transparent to-blue-700 opacity-20"></div>
                          </>
                        )}

                        {/* Icon container with gradient background when active */}
                        <div
                          className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300
                        ${
                          activeItem === item.id
                            ? `bg-gradient-to-br from-amber-400 to-amber-500 shadow-lg shadow-amber-500/20`
                            : "bg-blue-800 group-hover:bg-blue-700"
                        }`}
                        >
                          <div
                            className={`${activeItem === item.id ? "text-white animate-pulse" : "text-blue-300 group-hover:text-white"}`}
                          >
                            {item.icon}
                          </div>
                        </div>

                        {!collapsed && (
                          <div className="ml-3 flex flex-col">
                            {item.parent && (
                              <span className="text-xs text-blue-400">
                                {item.parent}
                              </span>
                            )}
                            <span
                              className={`text-sm ${activeItem === item.id ? "font-medium" : ""}`}
                            >
                              {item.label}
                            </span>
                          </div>
                        )}

                        {/* Enhanced tooltip for collapsed mode */}
                        {collapsed && hoveredItem === item.id && (
                          <div className="absolute left-full ml-4 px-3 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white text-xs rounded-md z-10 whitespace-nowrap shadow-xl border border-blue-700 animate-fadeIn">
                            <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-blue-900"></div>
                            <div className="font-medium bg-gradient-to-r from-amber-300 to-amber-400 bg-clip-text text-transparent">
                              {item.parent
                                ? `${item.parent} / ${item.label}`
                                : item.label}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* User Role Display - Enhanced with better styling */}
      {!collapsed && userRoleName && (
        <div className="px-4 py-2 border-t border-blue-700 bg-blue-800/30">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <div className="text-xs text-blue-300">
              {translations[language].Role}:{" "}
              <span className="text-amber-400 font-medium">{userRoleName}</span>
            </div>
          </div>
        </div>
      )}

      {/* Version Display */}
      {/* {!collapsed && (
                <div
                    className="px-4 py-2 border-t border-blue-700 bg-blue-800/20 cursor-pointer hover:bg-blue-800/40 transition-all duration-200"
                    onMouseEnter={() => setHoveredItem('version')}
                    onMouseLeave={() => setHoveredItem(null)}
                >
                    <div className="flex items-center space-x-2">
                        <div className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-700">
                            <Info className="h-3 w-3 text-blue-300" />
                        </div>
                        <div className="text-xs text-blue-300">
                            <div className="flex items-center space-x-1">
                                <span>{translations[language].Version} - {APP_VERSION}</span>


                            </div>
                        </div>
                    </div>
                </div>
            )} */}

      {/* Collapsed version display tooltip */}
      {/* {collapsed && hoveredItem === 'version-collapsed' && (
                <div className="absolute left-full ml-4 px-3 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white text-xs rounded-md z-10 whitespace-nowrap shadow-xl border border-blue-700 animate-fadeIn">
                    <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-blue-900"></div>
                    <div className="font-medium">
                        <div>{translations[language].Version}  {APP_VERSION}</div>
                    </div>
                </div>
            )} */}

      {/* Debug info - Remove in production */}
      {/* {process.env.NODE_ENV === 'development' && !collapsed && (
                <div className="px-4 py-1 text-xs text-gray-400 border-t border-blue-700">
                    Debug: userRole = {userRole || 'null'}
                </div>
            )} */}

      {/* Logout Button - Updated with loading state */}
      <div
        className={`flex items-center px-4 py-4 border-t border-blue-700 cursor-pointer hover:bg-blue-800 text-blue-300 hover:text-white transition-all duration-200 group ${isLoggingOut ? "opacity-75 cursor-not-allowed" : ""}`}
        onMouseEnter={() => !isLoggingOut && setHoveredItem("logout")}
        onMouseLeave={() => setHoveredItem(null)}
        onClick={handleLogout}
      >
        <div
          className={`flex items-center justify-center w-8 h-8 rounded-full bg-blue-800 group-hover:bg-red-500 group-hover:rotate-12 transition-all duration-300 ${isLoggingOut ? "bg-red-500" : ""}`}
        >
          {isLoggingOut ? (
            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
          ) : (
            <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
          )}
        </div>
        {!collapsed && (
          <div className="ml-3 text-sm font-medium">
            {isLoggingOut
              ? translations[language]?.loggingOut || "Logging out..."
              : translations[language]?.logout || "Logout"}
          </div>
        )}
        {collapsed && hoveredItem === "logout" && !isLoggingOut && (
          <div className="absolute left-full ml-4 px-3 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white text-xs rounded-md z-10 whitespace-nowrap shadow-xl border border-blue-700 animate-fadeIn">
            <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-blue-900"></div>
            <div className="font-medium bg-gradient-to-r from-red-400 to-red-500 bg-clip-text text-transparent">
              {translations[language]?.logout || "Logout"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// User Component Icon
const User = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
};

export default Sidebar;